import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { User, Lock, AlertCircle, ArrowRight, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import LoginBackdrop from './login/LoginBackdrop';
import MfaStep from './login/MfaStep';
import RecoveryCodesStep from './login/RecoveryCodesStep';

// Deep links (e.g. a shared deal URL) are remembered by ProtectedRoute; land there after sign-in.
// Once authenticated the /login route renders <PostLoginRedirect/> (App.js), which performs the redirect itself.
const afterLogin = () => { const s = sessionStorage.getItem('post_login_redirect'); return s && s.startsWith('/') && !s.startsWith('//') ? s : '/'; };
import { Field, PrimaryButton } from './login/LoginControls';

const TITLES = { 1: ['Welcome back', 'Sign in to your DerwentAI workspace'], 2: ['Verify your identity', null], 3: ['Recovery codes', 'Keep these somewhere safe'] };

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, verifyMFA, recoveryLogin } = useAuth();

  const [step, setStep] = useState(1); // 1: credentials, 2: MFA, 3: recovery codes issued
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mfaData, setMfaData] = useState(null);
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [issuedCodes, setIssuedCodes] = useState(null);
  const [codesSaved, setCodesSaved] = useState(false);

  const run = async (fn) => {
    setError(''); setLoading(true);
    try { await fn(); } catch (err) { setError(err.message || 'Something went wrong. Please try again.'); } finally { setLoading(false); }
  };

  const handleCredentialsSubmit = (e) => { e.preventDefault(); run(async () => {
    const data = await login(email, password);
    if (data.mfa_required) { setMfaData(data); setStep(2); }
  }); };

  const handleMFASubmit = (e) => { e.preventDefault(); run(async () => {
    const data = await verifyMFA(email, mfaCode);
    if (data?.recovery_codes?.length) { setIssuedCodes(data.recovery_codes); setStep(3); }
  }); };

  const handleRecoverySubmit = (e) => { e?.preventDefault?.(); run(async () => { await recoveryLogin(email, password, recoveryCode); }); };

  const handleBackToLogin = () => { setStep(1); setMfaCode(''); setError(''); setMfaData(null); setRecoveryMode(false); };

  const [title, subtitle] = TITLES[step];
  const mfaSubtitle = mfaData?.sms_sent ? `Enter the code sent to ****${mfaData?.phone_last4}` : 'Enter your verification code';

  return (
    <div className="relative min-h-screen font-sf text-slate-900 flex flex-col">
      <LoginBackdrop />

      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 py-10 sm:py-14">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-[420px]"
        >
          {/* Brand */}
          <div className="flex flex-col items-center mb-7 sm:mb-8 px-2">
            <img
              src="/derwent-ai-logo-white.png"
              alt="DerwentAI"
              className="block w-auto max-w-[240px] sm:max-w-[280px] h-auto select-none drop-shadow-[0_2px_12px_rgba(0,0,0,0.35)]"
              draggable={false}
              data-testid="login-logo"
            />
            <p className="mt-4 text-[13px] sm:text-[13.5px] text-white/60 tracking-[0.02em]">Broker intelligence platform</p>
          </div>

          {/* Card */}
          <div
            className="rounded-[22px] bg-white/[0.94] backdrop-blur-2xl ring-1 ring-white/50 shadow-[0_30px_80px_-20px_rgba(2,8,24,0.65),0_2px_6px_rgba(2,8,24,0.15)] px-6 py-7 sm:px-9 sm:py-9"
            data-testid="login-card"
          >
            <div className="mb-6">
              <h1 className="text-[22px] sm:text-[24px] font-bold tracking-tight text-slate-900 leading-tight" data-testid="login-title">{title}</h1>
              <p className="text-[13.5px] text-slate-500 mt-1.5">{step === 2 ? mfaSubtitle : subtitle}</p>
            </div>

            {error && (
              <div className="mb-5 px-3.5 py-3 rounded-xl bg-rose-50 border border-rose-200 flex items-start gap-2.5 text-rose-700 text-[13px]" role="alert" data-testid="login-error">
                <AlertCircle className="w-4 h-4 shrink-0 mt-[1px]" />
                <span>{error}</span>
              </div>
            )}

            {step === 1 && (
              <form onSubmit={handleCredentialsSubmit} className="space-y-4" data-testid="login-form">
                <Field
                  label="Username"
                  icon={User}
                  type="text"
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="username"
                  spellCheck={false}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your username"
                  required
                  data-testid="login-email"
                />
                <Field
                  label="Password"
                  icon={Lock}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  data-testid="login-password"
                  trailing={
                    <button type="button" onClick={() => setShowPassword((v) => !v)} className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors" aria-label={showPassword ? 'Hide password' : 'Show password'} data-testid="login-toggle-password">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  }
                />
                <PrimaryButton loading={loading} loadingLabel="Signing in..." className="mt-2" data-testid="login-submit">
                  Continue <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                </PrimaryButton>
              </form>
            )}

            {step === 2 && (
              <MfaStep
                mfaData={mfaData} mfaCode={mfaCode} setMfaCode={setMfaCode} onSubmit={handleMFASubmit} onBack={handleBackToLogin} loading={loading}
                recoveryMode={recoveryMode} setRecoveryMode={setRecoveryMode} recoveryCode={recoveryCode} setRecoveryCode={setRecoveryCode} onRecoverySubmit={handleRecoverySubmit}
              />
            )}

            {step === 3 && (
              <RecoveryCodesStep codes={issuedCodes} email={email} saved={codesSaved} setSaved={setCodesSaved} onContinue={() => navigate(afterLogin())} />
            )}
          </div>

          <p className="mt-6 flex items-center justify-center gap-1.5 text-[12.5px] text-white/55" data-testid="login-footnote">
            <ShieldCheck className="w-3.5 h-3.5 text-white/60" /> Protected by two-factor authentication
          </p>
        </motion.div>
      </div>

      <footer className="relative z-10 pb-5 text-center text-[11.5px] text-white/35">© {new Date().getFullYear()} Derwent Finance · DerwentAI</footer>
    </div>
  );
};

export default LoginPage;
